import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, retry, map } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { CiQuestionApiResponse } from '../_components/ci/models/ci-question.model';

/** Mirrors the admin form's PropertyCategory; stored so edit reopens the same flow. */
export type CiPropertyCategory = 'primary-project' | 'resale' | 'plot-land' | 'independent-house';

/** Plot / house inputs; stored with the CI so an edit reopens with them. */
export interface CiLandValue {
  landRatePsf: number | null;
  /** Asking Rate (₹/sq ft) = Asking Price ÷ Plot Area; calculated by the backend, read-only. */
  askingRatePsf?: number | null;
  roadWidthFt: number | null;
  cornerPlot: boolean;
  frontageFt: number | null;
  zoning: string | null;
  orientation?: string | null;
  /** Resolved by the backend from orientation / road width; read-only. */
  orientationAdjustment?: number | null;
  roadAdjustment?: number | null;
  premiumAdjustment?: number | null;
  supplyOverride?: boolean | null;
  /** Replacement Final Land Value total in ₹; internal. */
  supplyOverrideValue?: number | null;
  /** Mandatory with an override; internal. */
  supplyOverrideReason?: string | null;
}

export interface CiBuildingValue {
  builtUpArea: number | null;
  ageYears: number | null;
  constructionClass?: string | null;
  buildingRatePsf?: number | null;
  floors: number | null;
  /** Legacy fields from the replacement-cost model; no longer sent. */
  constructionRatePsf?: number | null;
  condition?: string | null;
}

/** Plot / house calculation breakdown returned by the backend. */
export interface CiLandAssessment {
  landRatePsf: number;
  plotArea: number;
  baseLandValue: number;
  orientation: string | null;
  orientationAdjustment: number;
  roadWidthFt: number | null;
  roadAdjustment: number;
  premiumAdjustment: number;
  totalLandAdjustment: number;
  adjustedLandValue: number;
  supplyOverrideApplied: boolean;
  supplyOverrideValue?: number | null;
  supplyOverrideReason?: string | null;
  finalLandValue: number;
  builtUpArea: number | null;
  buildingAgeYears: number | null;
  constructionClass: string | null;
  buildingAgeBand: string | null;
  buildingRatePsf: number | null;
  totalBuildingValue: number | null;
  assessedValue: number;
  landShare: number;
  askVsCiPercent: number;
}

export interface TransactionRow {
  price: number | null;
  source: string | null;
  date: string | null;
}

export interface CalculateCiRequest {
  userId?: string;
  propertyId?: string;

  propertyName?: string | null;
  microMarket?: string | null;
  cluster?: string | null;

  /** Primary only — "First Listed Configuration / Unit 1". */
  configuration?: string | null;
  /** Resale only — "Brigade Exotica · CIR-4821 · 4 BHK + Servant". */
  listingUnitName?: string | null;
  propertyCategory?: CiPropertyCategory | null;
  landValue?: CiLandValue | null;
  buildingValue?: CiBuildingValue | null;

  quotedPricePerSqFt?: string | null;
  quotedPriceOfUnit?: string | null;

  quotedUnitPrice: number;
  size: number;

  lastFiveTransactions: TransactionRow[];

  uds?: number | null;
  currentRatePerSqFtForUDS?: string | null;
  currentUdsRatePerSqft?: number | null;

  averageRentPerMonth?: number | null;

  trophyOverride?: boolean;

  locality?: string | null;
  builderTier?: string | null;
  legalIssueStatus?: string | null;
  scarcity?: string | null;
  samePropertyOverpricing?: string | null;
}

export interface SaveCiRequest {
  tempCalculationId: string;
  userId?: string;
  propertyId?: string;
  /** The CI being edited; the backend updates it instead of creating a new one. */
  ciRecordId?: string;
}

/** One row of POST /ci/bulk-upload that produced a CI record. */
export interface BulkCiRowSuccess {
  row: number;
  propertyName: string | null;
  listingUnitName: string | null;
  propertyCategory: string | null;
  propertyId: string | null;
  ciRecordId: string;
  status: 'DRAFT' | 'GENERATED';
  benchmarkPsf: number;
  adjustedVerdict: string;
}

/** One Excel row that failed, with its row number and every reason. */
export interface BulkCiRowFailure {
  row: number;
  propertyName: string | null;
  listingUnitName: string | null;
  propertyCategory: string | null;
  errors: string[];
}

export interface BulkCiUploadResult {
  totalRows: number;
  succeeded: number;
  failed: number;
  results: BulkCiRowSuccess[];
  errors: BulkCiRowFailure[];
}

export interface BulkCiUploadOptions {
  userId?: string | null;
  /** GENERATED (default) runs Generate CI for every row; DRAFT saves drafts. */
  status?: 'DRAFT' | 'GENERATED';
}

export interface DashboardQuery {
  userId?: string;
  /** 1-based page of recentIntel. */
  page?: number;
  limit?: number;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

export interface ChecklistWeightages {
  locality: number | null;
  builderTier: number | null;
  legalIssue: number | null;
  scarcity: number | null;
  samePropertyOverpricing: number | null;
}

export interface CiCalculationResult {
  _id: string;

  userId?: string;
  propertyId?: string;

  propertyName?: string | null;
  microMarket?: string | null;
  cluster?: string | null;

  /** Primary only — "First Listed Configuration / Unit 1". */
  configuration?: string | null;
  /** Resale only — "Brigade Exotica · CIR-4821 · 4 BHK + Servant". */
  listingUnitName?: string | null;
  propertyCategory?: CiPropertyCategory | null;
  landValue?: CiLandValue | null;
  buildingValue?: CiBuildingValue | null;

  quotedPricePerSqFt?: string | null;
  quotedPriceOfUnit?: string | null;

  quotedUnitPrice: number;
  size: number;

  lastFiveTransactions?: TransactionRow[] | null;

  last5TransactionsAvgPsf: number;
  lastAvgTransactedPricePerSqFtLast5Transaction?: string | null;
  lastTransactionPsf: number;
  lastTransactedPricePSF?: string | null;
  lastTransactionAgeMonths: number;
  dateOfLastTransactedPrice?: string | null;

  uds?: number | null;
  currentRatePerSqFtForUDS?: string | null;
  currentUdsRatePerSqft?: number | null;

  averageRentPerMonth?: number | null;
  averageRentPa?: number | null;
  rentalYield?: number | null;

  adjustedCurrentValueOfUds?: number | null;
  udsSupportPercent?: number | null;

  locality?: string | null;
  builderTier?: string | null;
  legalIssueStatus?: string | null;
  scarcity?: string | null;
  samePropertyOverpricing?: string | null;
  checklistWeightages?: ChecklistWeightages | null;
  totalChecklistWeightagePercent?: number | null;

  quotedPsf: number;
  benchmarkPsf: number;
  benchmarkLogicUsed: string;
  askVsBenchmarkPercent: number;
  revisedBenchmark?: number | null;

  trophyOverride: boolean;
  initialVerdict: string;
  adjustedVerdict: string;
  verdictDescription: string;
  circledIntelligenceVerdict: string;

  suggestedOfferPsf?: number | null;
  closePsfLow?: number | null;
  closePsfHigh?: number | null;

  suggestedOfferValue?: number | null;
  likelyCloseValueLow?: number | null;
  likelyCloseValueHigh?: number | null;

  fairValueBandLow?: number | null;
  fairValueBandHigh?: number | null;

  websiteSuggestedOffer?: string | null;
  websiteLikelyCloseBand?: string | null;

  /** Generated by the calculate API for every property; never typed by the admin. */
  analystNotes?: string | null;

  /** Plot / house breakdown; null for apartments. */
  landAssessment?: CiLandAssessment | null;

  createdAt: string;
  updatedAt: string;
}

export interface CiRecord extends CiCalculationResult {
  status: 'DRAFT' | 'GENERATED';
  tempCalculationId: string;
}

export interface IntelRecord {
  id: string;
  /** Present on newer dashboard responses; links the CI directly to inventory. */
  propertyId?: string | null;
  property: string;
  microMarket: string | null;
  quotedPrice: string;
  benchmarkPSF: string;
  verdict: string;
  lastUpdated: string;
  status: 'Completed' | 'Draft';
}

/** Prefer a generated CI over any draft, then the latest record for that property. */
export function uniquePropertyIntelligence(records: IntelRecord[]): IntelRecord[] {
  const normalize = (value: string | null) => (value ?? '').trim().toLowerCase();
  const legacyKey = (record: IntelRecord) =>
    JSON.stringify([normalize(record.property), normalize(record.microMarket)]);
  const idsByName = new Map<string, Set<string>>();
  for (const record of records) {
    if (!record.propertyId) continue;
    const key = legacyKey(record);
    const ids = idsByName.get(key) ?? new Set<string>();
    ids.add(record.propertyId);
    idsByName.set(key, ids);
  }
  const unique = new Map<string, IntelRecord>();
  const timestamp = (record: IntelRecord) => Date.parse(record.lastUpdated) || 0;
  for (const record of records) {
    const name = legacyKey(record);
    const candidates = idsByName.get(name);
    const propertyId = record.propertyId || (candidates?.size === 1 ? [...candidates][0] : null);
    const key = propertyId ? `id:${propertyId}` : `legacy:${name || record.id}`;
    const previous = unique.get(key);
    if (!previous ||
        (record.status === 'Completed' && previous.status !== 'Completed') ||
        (record.status === previous.status && timestamp(record) > timestamp(previous))) {
      unique.set(key, record);
    }
  }
  return [...unique.values()].sort((a, b) => timestamp(b) - timestamp(a));
}

export interface CiDashboardStats {
  propertiesAnalyzed: number;
  pendingAnalysis: number;
  lastAnalysisDate: string | null;
  lastAnalysisProperty: string | null;
}

export interface CiDashboardPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface CiDashboardData {
  stats: CiDashboardStats;
  recentIntel: IntelRecord[];
  /** Present on paginated responses; the backend then already dedupes rows and stats. */
  pagination?: CiDashboardPagination;
}

/** Cards describe the same unique properties as the returned dashboard list. */
export function normalizeCiDashboard(data: CiDashboardData): CiDashboardData {
  // A page holds only part of the list, so its stats must come from the backend.
  if (data.pagination) return data;
  const recentIntel = uniquePropertyIntelligence(data.recentIntel);
  const generated = recentIntel.filter((record) => record.status === 'Completed');
  return {
    ...data,
    recentIntel,
    stats: {
      propertiesAnalyzed: generated.length,
      pendingAnalysis: recentIntel.filter((record) => record.status === 'Draft').length,
      lastAnalysisDate: generated[0]?.lastUpdated ?? null,
      lastAnalysisProperty: generated[0]?.property ?? null,
    },
  };
}

// Mirror the backend enums exactly: the calculate API accepts only these
// values, which are the option labels served by the ci-questions collection.
export enum LocalityRating {
  PREMIUM = 'Premium pocket / strong location',
  GOOD = 'Good location',
  AVERAGE = 'Average / neutral location',
  WEAK = 'Weak pocket / poor access',
  NEGATIVE = 'Negative surroundings / bad approach',
}

export enum BuilderTier {
  TIER_1 = 'Tier 1 builder / strong brand',
  TIER_2 = 'Tier 2 builder / reputed local brand',
  AVERAGE = 'Average builder',
  UNKNOWN = 'Unknown builder',
  POOR = 'Poor builder reputation / delivery issues',
}

export enum LegalIssueStatus {
  CLEAR_TITLE_OC_AVAILABLE = 'Clear title + OC available',
  CLEAR_TITLE_OC_PENDING = 'Clear title, OC pending',
  MINOR_DOCUMENTATION_GAP = 'Minor documentation gap',
  NO_OC_APPROVAL_CONCERN = 'No OC / approval concern',
  MAJOR_LEGAL_ISSUE = 'Major legal issue / unclear title',
}

export enum ScarcityRating {
  RARE = 'Rare unit in project / micro-market',
  LIMITED = 'Limited similar inventory',
  NORMAL = 'Normal availability',
  MANY = 'Many similar units available',
}

export enum SamePropertyOverpricingRating {
  IN_LINE = 'Not overpriced / in line with other units',
  SLIGHTLY = 'Slightly overpriced',
  MODERATELY = 'Moderately overpriced',
  HIGHLY = 'Highly overpriced',
  VERY = 'Very overpriced / unjustified premium',
}

@Injectable({
  providedIn: 'root',
})
export class CiService {
  private readonly baseUrl = `${environment.api_URL}/ci`;

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  getDashboardData(query?: DashboardQuery): Observable<CiDashboardData> {
    let params = new HttpParams();

    if (query?.userId) {
      params = params.set('userId', query.userId);
    }

    if (query?.page) {
      params = params.set('page', query.page.toString());
    }

    if (query?.limit) {
      params = params.set('limit', query.limit.toString());
    }

    return this.http
      .get<ApiResponse<CiDashboardData>>(`${this.baseUrl}/dashboard`, { params })
      .pipe(
        retry(1),
        map((res) => normalizeCiDashboard(res.data)),
        catchError((error: HttpErrorResponse) =>
          throwError(() => ({
            status: false,
            message: this.getErrorMessage(error),
            error,
          })),
        ),
      );
  }
  /** Checklist questions (GET /ci-questions — a sibling of /ci, not under it). */
  getCIQns(): Observable<CiQuestionApiResponse[]> {
    return this.http
      .get<unknown>(`${environment.api_URL}/ci-questions`)
      .pipe(
        // Bare array, or wrapped as { data | questions | items: [...] } (optionally nested under data).
        map((res) => {
          const r = res as any;
          const list = [r, r?.data, r?.questions, r?.items, r?.data?.questions, r?.data?.items].find(
            (c) => Array.isArray(c),
          ) as CiQuestionApiResponse[] | undefined;
          if (!list?.length) {
            console.warn('[CI] /ci-questions returned no questions. Raw response:', res);
          }
          return list ?? [];
        }),
        catchError((error: HttpErrorResponse) =>
          throwError(() => ({
            status: false,
            message: this.getErrorMessage(error),
            error,
          })),
        ),
      );
  }

  calculate(payload: CalculateCiRequest): Observable<CiCalculationResult> {
    return this.http
      .post<ApiResponse<CiCalculationResult>>(
        `${this.baseUrl}/calculate`,
        payload
      )
      .pipe(
        map((res) => res.data),
        catchError((error: HttpErrorResponse) => {
          console.warn('[CI] calculate rejected. Payload sent:', payload);
          return throwError(() => ({
            status: false,
            message: this.getErrorMessage(error),
            error,
          }));
        }),
      );
  }


  saveDraft(payload: SaveCiRequest): Observable<CiRecord> {
    return this.http
      .post<ApiResponse<CiRecord>>(
        `${this.baseUrl}/draft`,
        payload
      )
      .pipe(
        map((res) => res.data),
        catchError((error: HttpErrorResponse) =>
          throwError(() => ({
            status: false,
            message: this.getErrorMessage(error),
            error,
          })),
        ),
      );
  }

  generateCi(payload: SaveCiRequest): Observable<CiRecord> {
    return this.http
      .post<ApiResponse<CiRecord>>(
        `${this.baseUrl}/generate`,
        payload
      )
      .pipe(
        map((res) => res.data),
        catchError((error: HttpErrorResponse) =>
          throwError(() => ({
            status: false,
            message: this.getErrorMessage(error),
            error,
          })),
        ),
      );
  }

  /** Uploads an .xlsx; each valid row is calculated and saved like a manual CI. */
  bulkUpload(file: File, options: BulkCiUploadOptions = {}): Observable<BulkCiUploadResult> {
    const body = new FormData();
    body.append('file', file, file.name);
    if (options.userId) body.append('userId', options.userId);
    if (options.status) body.append('status', options.status);

    return this.http
      .post<ApiResponse<BulkCiUploadResult>>(`${this.baseUrl}/bulk-upload`, body)
      .pipe(
        map((res) => res.data),
        catchError((error: HttpErrorResponse) =>
          throwError(() => ({
            status: false,
            message: this.getErrorMessage(error),
            error,
          })),
        ),
      );
  }

  /** The Excel template with the columns POST /ci/bulk-upload reads. */
  downloadBulkTemplate(): Observable<Blob> {
    return this.http
      .get(`${this.baseUrl}/bulk-upload/template`, { responseType: 'blob' })
      .pipe(
        catchError((error: HttpErrorResponse) =>
          throwError(() => ({
            status: false,
            message: this.getErrorMessage(error),
            error,
          })),
        ),
      );
  }

  /**
   * Single-sheet bulk-upload template with a row per unit of the selected
   * properties (ID, name, category, unit, configuration, micro market,
   * cluster, quoted price and size filled in).
   */
  downloadPrefilledBulkTemplate(propertyIds: string[]): Observable<Blob> {
    return this.http
      .post(`${this.baseUrl}/bulk-upload/template`, { propertyIds }, { responseType: 'blob' })
      .pipe(
        catchError((error: HttpErrorResponse) =>
          throwError(() => ({
            status: false,
            message: this.getErrorMessage(error),
            error,
          })),
        ),
      );
  }

  getCiRecordById(id: string): Observable<CiRecord> {
    return this.http
      .get<ApiResponse<CiRecord>>(`${this.baseUrl}/${id}`)
      .pipe(
        retry(1),
        map((res) => res.data),
        catchError((error: HttpErrorResponse) =>
          throwError(() => ({
            status: false,
            message: this.getErrorMessage(error),
            error,
          })),
        ),
      );
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    if (error.status === 0) {
      return 'Network error. Please check your connection.';
    }

    if (error.status === 400 && error.error?.message) {
      const msg = error.error.message;
      return Array.isArray(msg) ? msg.join(', ') : msg;
    }

    if (error.status === 401) return 'Unauthorised. Please log in again.';
    if (error.status === 403) return 'Access denied.';
    if (error.status === 404) {
      return error.error?.message ?? 'Record not found.';
    }
    if (error.status >= 500) {
      return 'Server error. Please try again later.';
    }

    return 'An unexpected error occurred. Please try again.';
  }
}
